import { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { getIncarnated } from '../persistence';
import { useIsMutationPending } from '../../../utils/rtkq/useMutationPending';

export const useIsLoggedIn = () => {
  const { data } = useAuthContext();
  // Always check localStorage - if token was cleared by another tab or 401 handler,
  // we should consider user logged out regardless of cached data
  const hasToken = typeof window !== 'undefined' ? !!localStorage.getItem('token') : false;

  // If localStorage has no token, user is definitely logged out
  if (!hasToken) {
    return false;
  }

  // If we have a token, use the cached loggedIn status if available
  if (typeof data?.loggedIn === 'boolean') {
    return data.loggedIn;
  }

  // Fallback: have token but status hasn't resolved yet - assume logged in
  return true;
};

export const useAuthToken = () => {
  const { data } = useAuthContext();
  // Always check localStorage first - if token was cleared, we're logged out
  const lsToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  if (!lsToken) {
    return null;
  }

  // Use cached sessionId if available, otherwise use localStorage token
  return data?.sessionId || lsToken;
};

export const useUser = () => {
  const { data } = useAuthContext();
  const isValidUser = (u) => !!u && (u.id != null || !!u.username || !!u.displayName);
  if (isValidUser(data?.user)) {
    return data.user;
  }
  if (typeof window !== 'undefined') {
    try {
      const lsUser = JSON.parse(localStorage.getItem('user') || 'null');
      return isValidUser(lsUser) ? lsUser : null;
    } catch {
      return null;
    }
  }
  return null;
};

export const useRoles = () => {
  const { data } = useAuthContext();
  let roles = Array.isArray(data?.roles) && data.roles.length ? data.roles : null;
  if (!roles && typeof window !== 'undefined') {
    try {
      const lsRoles = JSON.parse(localStorage.getItem('roles') || 'null');
      roles = Array.isArray(lsRoles) && lsRoles.length ? lsRoles : null;
    } catch {
      roles = null;
    }
  }
  return roles;
};

export const useUserRole = () => {
  const roles = useRoles();
  if (!roles) return null;
  return roles[0] === 'tester' && roles.length > 2 ? roles[1] : roles[0];
};

export const useIsIncarnated = () => {
  const [value, setValue] = useState(getIncarnated());
  useEffect(() => {
    const onChange = (e) => setValue(!!e?.detail || getIncarnated());
    window.addEventListener('incarnated-changed', onChange);
    return () => window.removeEventListener('incarnated-changed', onChange);
  }, []);
  return value;
};

export const useAuthLoading = () => {
  const { isLoading, isTransitioning } = useAuthContext();
  return { isAuthLoading: isLoading || isTransitioning };
};

export const useIsLoginPending = () => {
  return useIsMutationPending('login');
};

export const useIsLogoutPending = () => {
  return useIsMutationPending('logout');
};

export const useIsAuthMutationPending = () => {
  return useIsMutationPending((m) =>
    ['login', 'logout', 'incarnate', 'wakeUp', 'registrationAndLogin', 'guestRegister', 'hashLogin', 'loginGoogle'].includes(m?.endpointName)
  );
};

export const useLocalization = () => {
  const { data } = useAuthContext();
  return useMemo(() => {
    // Prefer full locale from user when available and supported
    const supported = new Set(['da_DK', 'en_US', 'fr_FR', 'de_DE', 'fi_FI', 'nb_NO', 'pl_PL', 'sv_SE', 'nl_NL']);

    const userLocale = data?.user?.locale || null; // e.g., 'sv_SE'
    if (userLocale && supported.has(userLocale)) return userLocale;

    // Fallbacks: map 2-letter codes and known variants to supported full codes
    const short = (data?.user?.locale || '').slice(0, 2).toLowerCase();
    const role = data?.roles?.[0];

    // Special-case previously used 'en-gb' hint: normalize to en_US resources
    if (role === 'assistant' && short === 'en') return 'en_US';

    const map = {
      da: 'da_DK',
      en: 'en_US',
      fr: 'fr_FR',
      de: 'de_DE',
      fi: 'fi_FI',
      nb: 'nb_NO',
      pl: 'pl_PL',
      sv: 'sv_SE',
      nl: 'nl_NL',
    };
    return map[short] || null;
  }, [data?.user?.locale, data?.roles]);
};

// New: simple admin check derived from current role
export const useIsAdmin = () => {
  const role = useUserRole();
  return role === 'dark-admin' || role === 'assistant';
};

// New: role gating helper to mirror legacy selectHasRequiredLevel
export const useHasRequiredLevel = (requiredLevel) => {
  const role = useUserRole();
  if (!role) return false;
  switch (requiredLevel) {
    case 'admin':
      return ['assistant', 'dark-admin'].includes(role);
    case 'owner':
      return ['assistant', 'dark-admin', 'owner'].includes(role);
    case 'teacher':
      return ['assistant', 'dark-admin', 'owner', 'teacher'].includes(role);
    case 'accountant':
      return ['assistant', 'dark-admin', 'owner', 'accountant'].includes(role);
    case 'editor':
      return ['assistant', 'dark-admin', 'owner', 'editor'].includes(role);
    default:
      return false;
  }
};
